import { FC, useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { Form, handleServerErrors } from 'pagesNew/PracticeTests/Form'
import { useLink } from 'routes/cmsRoutes'
import { CREATE_PRACTICE_TEST } from 'queries/practiceTest'
import { FIND_ONE_SUBJECT } from 'queries/subject'
import { CreatePracticeTestInput, PracticeTestTypeEnum, TimeRecordEnum } from '__generatedTypes__/globalTypes'
import { createPracticeTest, createPracticeTestVariables } from '__generatedTypes__/createPracticeTest'
import { findOneSubject, findOneSubjectVariables } from '__generatedTypes__/findOneSubject'
import { FIND_SCHOOL_LIST_BY_PLATFORM } from 'queries/faculty'
import {
  findSchoolListByPlatform,
  findSchoolListByPlatformVariables,
} from '__generatedTypes__/findSchoolListByPlatform'
import { useHistory } from 'hooks/useHistory'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { PlatformSlug } from 'constants/consts'
import { CourseName } from 'theme/utils'
import { School } from 'pagesNew/Users/Edit/components/hooks'
import { COMMON_PTS_CONFIG } from '../consts'

export const Create: FC<{ type?: PracticeTestTypeEnum }> = ({ type = PracticeTestTypeEnum.PracticeTest }) => {
  const config = useMemo(() => COMMON_PTS_CONFIG[type], [type])
  const { navigate } = useLink()
  const [searchParams] = useSearchParams()
  const subjectId = searchParams.get('subjectId')
  const practiceTestCategoryId = searchParams.get('categoryId')
  const defaultOrder = +(searchParams.get('defaultOrder') ?? 0)
  const { loadingHistory, populateHistoryWithUser } = useHistory()
  const [create] = useMutation<createPracticeTest, createPracticeTestVariables>(CREATE_PRACTICE_TEST)
  const subjectState = useQuery<findOneSubject, findOneSubjectVariables>(FIND_ONE_SUBJECT, {
    variables: {
      id: String(subjectId),
    },
  })
  const subject = subjectState.data?.findOneSubject
  const title = config?.titleSingle ?? ''
  const dashboardLink = config?.dashboardLink ?? ''

  const isInPersonTest = [PracticeTestTypeEnum.InPersonMockTest].includes(type)
  const schoolsState = useQuery<findSchoolListByPlatform, findSchoolListByPlatformVariables>(
    FIND_SCHOOL_LIST_BY_PLATFORM,
    {
      variables: {
        platformId: String(subject?.platform.id),
      },
      skip: !isInPersonTest || !subject?.platform.id,
      fetchPolicy: 'cache-and-network',
    }
  )
  const schools = useMemo(
    () =>
      schoolsState.data?.findSchoolListByPlatform?.schoolArray?.map(
        (school) =>
          ({
            id: school.key,
            title: school.value,
          } as School)
      ),
    [schoolsState.data?.findSchoolListByPlatform]
  )

  const loadingSchools = isInPersonTest && schoolsState.loading

  const handleSubmit = useCallback(
    async (practiceTest: CreatePracticeTestInput) => {
      try {
        const { data } = await create({
          variables: {
            params: {
              ...practiceTest,
              timeRecord: TimeRecordEnum.Timed,
              type,
              history: populateHistoryWithUser(),
            },
          },
        })

        if (data?.createPracticeTest.id) {
          navigate(dashboardLink)
          toast.success(`${title} created successfully`)
        } else {
          toast.error(`Failed to create ${title}`)
        }
      } catch (error) {
        toast.error(`Failed to create ${title}`)
        return handleServerErrors(error, title)
      }
    },
    [type, populateHistoryWithUser, create, navigate, subject]
  )

  useEffect(() => {
    if (!subjectId || (!subjectState.loading && !subject)) {
      navigate(dashboardLink)
      toast.error(`Subject not found, redirecting to ${title} list`)
    }
  }, [type, subjectId, subjectState.loading, subject])

  if (!subjectId) {
    return null
  }

  if (subjectState.loading || !subject || loadingHistory || loadingSchools) {
    return <PageLoader />
  }

  return (
    <Form
      type={type}
      title={title}
      practiceTestOrSubjectId={subjectId}
      practiceTestCategoryId={practiceTestCategoryId ?? undefined}
      subject={{
        ...subject,
        tabName:
          subject.platform.slug === PlatformSlug.CARSBooster
            ? CourseName.ReadingComprehension
            : CourseName.PerceptualAbility,
      }}
      tagLabel={subject.platform.slug.toUpperCase()}
      passFailStandardizedScore={[PracticeTestTypeEnum.MockTest, PracticeTestTypeEnum.InPersonMockTest].includes(type)}
      isInPersonTest={isInPersonTest}
      schools={schools ?? []}
      onSubmit={handleSubmit}
      defaultOrder={defaultOrder}
    />
  )
}
